import { DateTimeError } from "./errors.js";
import { DateTimeError as DateTimeError2 } from "./errors.js";
const OCCURRENCE_TIMING_WRITE_FIELDS = [
  "calendarTiming",
  "timeZone",
  "localStart",
  "localEnd",
  "startsAt",
  "endsAt",
  "startOverlap",
  "endOverlap",
  "zoneChangePolicy",
  "expectedTimingRevision"
];
function parseOccurrenceTimingWrite(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new DateTimeError("INVALID_TIMING", "Expected occurrence timing fields.");
  }
  const value = input;
  const result = {};
  const calendar = Object.hasOwn(value, "calendarTiming") && value.calendarTiming !== null;
  if (Object.hasOwn(value, "calendarTiming")) result.calendarTiming = calendar ? parseCalendarTiming(value.calendarTiming) : null;
  const boundary = (inputBoundary) => {
    if (!inputBoundary || typeof inputBoundary !== "object" || Array.isArray(inputBoundary)) {
      throw new DateTimeError("INVALID_TIMING", "A local boundary requires an explicit date and time.");
    }
    const item = inputBoundary;
    if (Object.keys(item).some((key) => key !== "date" && key !== "time")) {
      throw new DateTimeError("INVALID_TIMING", "Unknown local boundary field.");
    }
    return { date: parseCalendarDate(item.date), time: parseLocalTime(item.time) };
  };
  if (Object.hasOwn(value, "timeZone")) result.timeZone = calendar && value.timeZone === null ? null : parseTimeZone(value.timeZone);
  if (Object.hasOwn(value, "localStart")) result.localStart = calendar && value.localStart === null ? null : boundary(value.localStart);
  if (Object.hasOwn(value, "localEnd")) result.localEnd = value.localEnd === null ? null : boundary(value.localEnd);
  if (Object.hasOwn(value, "startsAt")) result.startsAt = calendar && value.startsAt === null ? null : parseInstant(value.startsAt);
  if (Object.hasOwn(value, "endsAt")) result.endsAt = value.endsAt === null ? null : parseInstant(value.endsAt);
  for (const key of ["startOverlap", "endOverlap"]) {
    if (!Object.hasOwn(value, key)) continue;
    if (!["reject", "earlier", "later"].includes(value[key])) {
      throw new DateTimeError("INVALID_TIMING", "An overlap choice must be reject, earlier or later.");
    }
    result[key] = value[key];
  }
  if (Object.hasOwn(value, "zoneChangePolicy")) {
    if (value.zoneChangePolicy !== "preserveInstant" && value.zoneChangePolicy !== "preserveLocal") {
      throw new DateTimeError("INVALID_TIMING", "Choose preserveInstant or preserveLocal when changing the saved timezone.");
    }
    result.zoneChangePolicy = value.zoneChangePolicy;
  }
  if (Object.hasOwn(value, "expectedTimingRevision")) {
    if (typeof value.expectedTimingRevision !== "number" || !Number.isSafeInteger(value.expectedTimingRevision) || value.expectedTimingRevision < 0) {
      throw new DateTimeError("INVALID_TIMING", "Expected a nonnegative integer timing revision.");
    }
    result.expectedTimingRevision = value.expectedTimingRevision;
  }
  if (calendar) {
    if (["localStart", "localEnd", "startsAt", "endsAt"].some((key) => value[key] !== void 0 && value[key] !== null) || ["time", "startTime", "endTime"].some((key) => value[key] !== void 0 && value[key] !== null) || ["startOverlap", "endOverlap", "zoneChangePolicy"].some((key) => Object.hasOwn(value, key))) {
      throw new DateTimeError("INVALID_TIMING", "Calendar timing cannot include clock boundaries, instants or DST/zone-change controls.");
    }
    if (result.calendarTiming?.kind === "allDay" && !result.timeZone) {
      throw new DateTimeError("INVALID_TIMING", "An all-day occurrence requires an explicit named timezone.");
    }
    if (result.calendarTiming?.kind === "dateOnly" && result.timeZone != null) {
      throw new DateTimeError("INVALID_TIMING", "A plain calendar date has no timezone.");
    }
  }
  return result;
}
function parseCalendarTiming(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) throw new DateTimeError("INVALID_TIMING", "Expected calendar timing.");
  const value = input;
  if (Object.keys(value).some((key) => !["kind", "startDate", "endDateExclusive"].includes(key)) || !["dateOnly", "tbc", "allDay"].includes(value.kind)) {
    throw new DateTimeError("INVALID_TIMING", "Invalid calendar timing fields.");
  }
  const startDate = parseCalendarDate(value.startDate);
  if (value.kind === "allDay") {
    const endDateExclusive = parseCalendarDate(value.endDateExclusive);
    if (endDateExclusive <= startDate) throw new DateTimeError("INVALID_TIMING", "All-day exclusive end must be after start date.");
    return { kind: "allDay", startDate, endDateExclusive };
  }
  if (value.endDateExclusive !== void 0 && value.endDateExclusive !== null) {
    throw new DateTimeError("INVALID_TIMING", "Date-only/TBC values have no all-day duration.");
  }
  return { kind: value.kind, startDate, endDateExclusive: null };
}
const zones = /* @__PURE__ */ new Map();
const DAY_MS = 864e5;
function parseCalendarDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new DateTimeError("INVALID_DATE", "Expected a calendar date in YYYY-MM-DD form.");
  }
  const year = Number(value.slice(0, 4)), month = Number(value.slice(5, 7)), day = Number(value.slice(8));
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] || 0;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > days) {
    throw new DateTimeError("INVALID_DATE", `Invalid Gregorian calendar date: ${value}.`);
  }
  return value;
}
function parseLocalTime(value) {
  if (typeof value !== "string" || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value)) {
    throw new DateTimeError("INVALID_TIME", "Expected a local clock time in HH:mm form.");
  }
  return value;
}
function parseInstant(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?Z$/.test(value)) {
    throw new DateTimeError("INVALID_INSTANT", "Expected a UTC timestamp ending in Z, with at most millisecond precision.");
  }
  try {
    parseCalendarDate(value.slice(0, 10));
  } catch {
    throw new DateTimeError("INVALID_INSTANT", "The timestamp contains an invalid calendar date.");
  }
  return new Date(value).toISOString();
}
function instantFromEpochMilliseconds(value) {
  if (!Number.isSafeInteger(value) || Math.abs(value) > 864e13) {
    throw new DateTimeError("INVALID_INSTANT", "Epoch milliseconds must be a finite, in-range integer.");
  }
  return parseInstant(new Date(value).toISOString());
}
function epochMilliseconds(value) {
  return Date.parse(parseInstant(value));
}
function parseTimeZone(value) {
  if (typeof value !== "string" || value !== "UTC" && !/^[A-Za-z_]+\/[A-Za-z0-9_+./-]+$/.test(value)) {
    throw new DateTimeError("INVALID_TIME_ZONE", "Expected a named IANA timezone (Area/Place) or UTC, not an offset or abbreviation.");
  }
  const cached = zones.get(value);
  if (cached) return cached;
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
  } catch {
    throw new DateTimeError("INVALID_TIME_ZONE", `Unsupported timezone: ${value}.`);
  }
  if (zones.size >= 64) zones.delete(zones.keys().next().value);
  const zone = value;
  zones.set(value, zone);
  return zone;
}
function parseDisplayPreference(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Expected date/time preferences.");
  }
  const value = input;
  if (Object.keys(value).some((key) => !["timeZoneMode", "timeZone", "hourCycle"].includes(key)) || value.timeZoneMode !== "device" && value.timeZoneMode !== "fixed" || value.hourCycle !== void 0 && value.hourCycle !== "system" && value.hourCycle !== "h12" && value.hourCycle !== "h23") {
    throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Invalid date/time preference fields.");
  }
  const hourCycle = value.hourCycle ?? "h12";
  if (value.timeZoneMode === "fixed") return { timeZoneMode: "fixed", timeZone: parseTimeZone(value.timeZone), hourCycle };
  if (value.timeZone != null) throw new DateTimeError("INVALID_DISPLAY_CONTEXT", "Device mode cannot save a fixed timezone.");
  return { timeZoneMode: "device", timeZone: null, hourCycle };
}
function addCalendarDays(date, days) {
  parseCalendarDate(date);
  if (!Number.isSafeInteger(days) || Math.abs(days) > 3652058) {
    throw new DateTimeError("INVALID_DATE", "Calendar day delta must be a bounded integer.");
  }
  const value = (/* @__PURE__ */ new Date(`${date}T00:00:00.000Z`)).getTime() + days * DAY_MS;
  return parseCalendarDate(instantFromEpochMilliseconds(value).slice(0, 10));
}
function invalid(message) {
  throw new DateTimeError("INVALID_RECURRENCE", message);
}
function record(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) invalid("Expected a schedule record.");
  return input;
}
function keys(value, allowed) {
  if (Object.keys(value).some((key) => !allowed.includes(key))) invalid("Unknown schedule field; check its spelling and schema version.");
}
function integer(input, min, max, name) {
  if (typeof input !== "number" || !Number.isInteger(input) || input < min || input > max) invalid(`${name} must be an integer from ${min} to ${max}.`);
  return input;
}
function overlap(input) {
  if (input !== "reject" && input !== "earlier" && input !== "later") invalid("An explicit reject/earlier/later overlap policy is required.");
  return input;
}
function end(input) {
  const value = record(input);
  keys(value, ["time", "dayOffset", "overlapPolicy"]);
  return {
    time: parseLocalTime(value.time),
    dayOffset: integer(value.dayOffset, 0, 31, "End day offset"),
    ...value.overlapPolicy === void 0 ? {} : { overlapPolicy: overlap(value.overlapPolicy) }
  };
}
function recurrence(input) {
  const value = record(input);
  if (value.frequency === "once") {
    keys(value, ["frequency"]);
    return { frequency: "once" };
  }
  if (value.frequency === "weekly") {
    keys(value, ["frequency", "interval"]);
    return { frequency: "weekly", interval: integer(value.interval, 1, 52, "Week interval") };
  }
  if (value.frequency !== "monthly") invalid("Supported frequencies are once, weekly and monthly.");
  keys(value, ["frequency", "interval", "pattern"]);
  const pattern = record(value.pattern);
  let parsed;
  if (pattern.kind === "dayOfMonth") {
    keys(pattern, ["kind", "day"]);
    parsed = { kind: "dayOfMonth", day: integer(pattern.day, 1, 31, "Day of month") };
  } else if (pattern.kind === "nthWeekday") {
    keys(pattern, ["kind", "weekday", "ordinal"]);
    const ordinal = pattern.ordinal === -1 ? -1 : integer(pattern.ordinal, 1, 5, "Weekday ordinal");
    parsed = { kind: "nthWeekday", weekday: integer(pattern.weekday, 1, 7, "ISO weekday"), ordinal };
  } else invalid("A monthly dayOfMonth or nthWeekday pattern is required.");
  return { frequency: "monthly", interval: integer(value.interval, 1, 120, "Month interval"), pattern: parsed };
}
function array(input) {
  if (!Array.isArray(input) || input.length > 1e3) invalid("Exclusions and overrides must be arrays with at most 1000 entries.");
  return input;
}
function parseScheduleFields(input) {
  const value = record(input);
  keys(value, ["version", "timeZone", "startDate", "startTime", "end", "recurrence", "gapPolicy", "overlapPolicy", "until", "count", "exclusions", "overrides"]);
  if (value.version !== 1) invalid("Only schedule schema version 1 is supported.");
  if (value.gapPolicy !== "skip" && value.gapPolicy !== "reject") invalid("An explicit skip/reject gap policy is required.");
  const schedule = {
    version: 1,
    timeZone: parseTimeZone(value.timeZone),
    startDate: parseCalendarDate(value.startDate),
    startTime: parseLocalTime(value.startTime),
    recurrence: recurrence(value.recurrence),
    gapPolicy: value.gapPolicy,
    overlapPolicy: overlap(value.overlapPolicy),
    exclusions: array(value.exclusions === void 0 ? [] : value.exclusions).map(parseCalendarDate),
    overrides: array(value.overrides === void 0 ? [] : value.overrides).map((inputOverride) => {
      const item = record(inputOverride);
      keys(item, ["originalDate", "date", "time", "end", "overlapPolicy"]);
      return {
        originalDate: parseCalendarDate(item.originalDate),
        date: parseCalendarDate(item.date),
        time: parseLocalTime(item.time),
        ...item.end === void 0 ? {} : { end: end(item.end) },
        ...item.overlapPolicy === void 0 ? {} : { overlapPolicy: overlap(item.overlapPolicy) }
      };
    }),
    ...value.end === void 0 ? {} : { end: end(value.end) },
    ...value.until === void 0 ? {} : { until: parseCalendarDate(value.until) },
    ...value.count === void 0 ? {} : { count: integer(value.count, 1, 1e4, "Occurrence count") }
  };
  if (schedule.until && schedule.until < schedule.startDate) invalid("Until must not precede the schedule anchor.");
  if (schedule.recurrence.frequency === "once" && schedule.count !== void 0 && schedule.count !== 1) invalid("A one-off schedule can have only one occurrence.");
  if (new Set(schedule.exclusions).size !== schedule.exclusions.length || new Set(schedule.overrides.map((item) => item.originalDate)).size !== schedule.overrides.length) {
    invalid("Duplicate exclusion or override dates are not allowed.");
  }
  return schedule;
}
export {
  DateTimeError2 as DateTimeError,
  OCCURRENCE_TIMING_WRITE_FIELDS,
  addCalendarDays,
  epochMilliseconds,
  instantFromEpochMilliseconds,
  parseCalendarDate,
  parseCalendarTiming,
  parseDisplayPreference,
  parseInstant,
  parseLocalTime,
  parseOccurrenceTimingWrite,
  parseScheduleFields,
  parseTimeZone
};
//# sourceMappingURL=validation.js.map
